// src/contexts/AuthContext.js - Fixed version to handle direct login
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { 
  signUp, 
  signIn, 
  signOut, 
  getCurrentUser, 
  getCurrentSession, 
  onAuthStateChange,
  getUserProfile,
  saveUserProfile,
  initializeUserSessions
} from '../supabase';

import { supabase } from '../supabase';
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Helper function to safely set loading state
  const safeSetLoading = useCallback((value) => {
    setLoading(value);
  }, []);

  const loadUserProfile = useCallback(async (currentUser) => {
  if (!currentUser) return null;
  
  console.log('⚠️ Skipping profile loading temporarily');
  
  const simpleProfile = {
    id: currentUser.id,
    email: currentUser.email,
    username: currentUser.email?.split('@')[0] || 'User',
    role: 'patient',
    status: 'active'
  };
  
  setUserProfile(simpleProfile);
  console.log('✅ Simple profile created');
  return simpleProfile;
}, []);


  // Initialize sessions safely
  const initializeSessions = useCallback(async (userId) => {
    if (!userId) return;

    try {
      console.log('🔄 Initializing sessions for user:', userId);
      await initializeUserSessions(userId);
      console.log('✅ Sessions initialized successfully');
    } catch (error) {
      console.warn('⚠️ Failed to initialize sessions:', error.message);
      // Don't block the auth flow for session initialization errors
    }
  }, []);

const checkStoredSession = useCallback(async () => {
  try {
    console.log('🔄 Checking stored session...');
    
    // Check if we have the auth token in localStorage
    const authToken = localStorage.getItem('sb-zquoaicefmsdeugdixvs-auth-token');
    
    if (authToken) {
      console.log('✅ Found auth token in localStorage');
      try {
        const sessionData = JSON.parse(authToken);
        if (sessionData.user && sessionData.access_token) {
          console.log('✅ Valid session found:', sessionData.user.email);
          return sessionData.user;
        }
      } catch (parseError) {
        console.error('❌ Error parsing stored session:', parseError);
      }
    }
    
    // Fallback to Supabase getSession (but this seems to be failing)
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('❌ Session check error:', error);
      return null;
    }
    
    if (session?.user) {
      console.log('✅ Found valid session from Supabase:', session.user.email);
      return session.user;
    }
    
    console.log('ℹ️ No valid session found');
    return null;
  } catch (error) {
    console.error('❌ Session check failed:', error);
    return null;
  }
}, []);

 // Update your initializeAuth function to properly handle the session
const initializeAuth = useCallback(async () => {
  if (authInitialized) return;

  try {
    console.log('🔄 AuthContext: Starting initialization...');
    
    // Set timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn('⏰ AuthContext: Initialization timeout, forcing completion');
      setLoading(false);
      setAuthInitialized(true);
    }, 8000);

    // First check for stored session
    const storedUser = await checkStoredSession();
    
    if (storedUser) {
      console.log('✅ Found stored user, setting auth state');
      setUser(storedUser);
      
      // Load profile
      const profile = await loadUserProfile(storedUser);
      if (profile) {
        setUserProfile(profile);
      }
      
      // Initialize sessions
      initializeSessions(storedUser.id);
    } else {
      console.log('ℹ️ No stored user found');
    }
    
    // Clear timeout since we completed successfully
    clearTimeout(timeoutId);
    
  } catch (error) {
    console.error('❌ Auth initialization error:', error);
    setError(error.message);
  } finally {
    setLoading(false);
    setAuthInitialized(true);
  }
}, [authInitialized, checkStoredSession, loadUserProfile, initializeSessions]);
  // Listen for auth state changes
  useEffect(() => {
    let subscription = null;

   // Replace your setupAuthListener function with this:
const setupAuthListener = async () => {
  try {
    console.log('🔄 Setting up auth state listener...');
    
    const { data } = onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state changed:', event, session?.user?.email || 'No user');
      
      if (event === 'SIGNED_IN' && session?.user) {
        console.log('✅ User signed in via auth state change:', session.user.email);
        setUser(session.user);
        setError(null);
        
        // Load profile
        const profile = await loadUserProfile(session.user);
        if (profile) {
          setUserProfile(profile);
        }
        
        // Initialize sessions
        initializeSessions(session.user.id);
        
        // IMPORTANT: Set loading to false here
        setLoading(false);
        
      } else if (event === 'SIGNED_OUT') {
        console.log('✅ User signed out');
        setUser(null);
        setUserProfile(null);
        setError(null);
        setLoading(false);
      } else if (event === 'INITIAL_SESSION') {
        // Handle initial session load
        setLoading(false);
      }
    });

    subscription = data;
  } catch (error) {
    console.error('❌ Error setting up auth listener:', error);
    setLoading(false);
  }
};

    setupAuthListener();

    return () => {
      if (subscription?.subscription) {
        subscription.subscription.unsubscribe();
      }
    };
  }, [loadUserProfile, initializeSessions]);

  useEffect(() => {
  if (!authInitialized) {
    initializeAuth();
  }
}, [initializeAuth, authInitialized]);

  // Sign up function
  const signUpUser = async (email, password, userData = {}) => {
    try {
      console.log('🔄 Signing up user:', email);
      setError(null);
      
      const result = await signUp(email, password, userData);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Sign up error:', error);
      setError(error.message);
      throw error;
    }
  };

  // Sign in function - FIXED to update context
  const signInUser = async (email, password) => {
    try {
      console.log('🔄 Signing in user:', email);
      setError(null);
      
      const result = await signIn(email, password);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      // IMPORTANT: Update the context state immediately
      if (result.user) {
        console.log('✅ Updating auth context with user:', result.user.email);
        setUser(result.user);
        
        // Load profile
        const profile = await loadUserProfile(result.user);
        if (profile) {
          setUserProfile(profile);
        }
        
        // Initialize sessions
        initializeSessions(result.user.id);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Sign in error:', error);
      setError(error.message);
      throw error;
    }
  };

  // Sign out function
  const signOutUser = async () => {
    try {
      console.log('🔄 Signing out user');
      setError(null);
      
      const result = await signOut();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      // Clear local state
      setUser(null);
      setUserProfile(null);
      
      return result;
    } catch (error) {
      console.error('❌ Sign out error:', error);
      setError(error.message);
      throw error;
    }
  };

  // Debug info
  console.log('🔍 AuthContext state:', {
    hasUser: !!user,
    userEmail: user?.email,
    hasProfile: !!userProfile,
    loading,
    error,
    authInitialized
  });

  const value = {
    user,
    userProfile,
    loading,
    error,
    signUp: signUpUser,
    signIn: signInUser,
    signOut: signOutUser,
    // Legacy aliases
    createUserWithEmailAndPassword: signUpUser,
    signInWithEmailAndPassword: signInUser,
    logout: signOutUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};